import { ArrowLeft, Edit, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import loggingService from '../../../services/loggingService';
import { h4, sh2 } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import IconButton from '../../ui/IconButton';
import IconTextButton from '../../ui/IconTextButton';
import Card from '../../ui/card';
import { tableBookingApi } from '../tableBookingApi';
import TablesSidePanel from './TablesSidePanel';

const INPUT_BASE_CLASS =
  'w-full rounded-[10px] bg-white/80 border border-gray-200 px-3 py-2 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-400/50 focus:border-green-400';

const OnboardTable = () => {
  // Log screen view
  useScreenLogging('OnboardTable', {
    component: 'OnboardTable',
    screenType: 'table-onboarding',
  });

  const [sites, setSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [site, setSite] = useState('');
  const [floor, setFloor] = useState('');
  const [block, setBlock] = useState('');
  const [tables, setTables] = useState([
    { id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' },
  ]);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState(null);
  const [showRightPanel, setShowRightPanel] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(true); // Control side panel visibility
  const [mode, setMode] = useState(null); // 'onboard' or 'view'
  const [isEditing, setIsEditing] = useState(false); // Track edit mode
  const [originalData, setOriginalData] = useState(null); // Store original data when editing
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [nextTableNumber, setNextTableNumber] = useState(1); // Continue from existing tables for site+floor
  const [isLoadingTablesForFloor, setIsLoadingTablesForFloor] = useState(false);
  const [showDeleteSectionConfirm, setShowDeleteSectionConfirm] = useState(false);
  const [isDeletingSection, setIsDeletingSection] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Key to trigger side panel refresh

  const maxTables = 20;

  // When site and floor are selected, fetch existing tables and set next table number to continue from
  useEffect(() => {
    if (!site || !floor) {
      setNextTableNumber(1);
      return;
    }

    const floorNum = Number(floor);
    if (isNaN(floorNum) || floorNum <= 0) {
      setNextTableNumber(1);
      return;
    }

    let cancelled = false;

    const loadTablesForSiteAndFloor = async () => {
      try {
        setIsLoadingTablesForFloor(true);
        const response = await tableBookingApi.getTablesWithFilters({
          site_id: site,
          floor_no: floorNum,
        });
        if (cancelled) return;

        const existingTables = response.tables || [];
        const maxTableNo =
          existingTables.length > 0
            ? Math.max(...existingTables.map((t) => t.table_no ?? 0))
            : 0;
        const next = maxTableNo + 1;
        setNextTableNumber(next);
        
        // Log successful API call
        loggingService.logInfo(
          'table_onboarding_load_tables',
          `Successfully loaded ${existingTables.length} tables for site ${site} floor ${floorNum}`,
          {
            endpoint: '/tables/tables',
            method: 'GET',
            siteId: site,
            floorNo: floorNum,
            tableCount: existingTables.length,
          }
        ).catch(logError => {
          console.warn('[OnboardTable] Failed to log event:', logError);
        });

        // If we have a single default row (no table_no), pre-fill it to continue numbering
        setTables((prev) => {
          if (
            prev.length === 1 &&
            (prev[0].table_no === undefined || prev[0].table_no === null)
          ) {
            return [
              {
                ...prev[0],
                tableNumber: String(next).padStart(2, '0'),
                tableLabel: `T-${next}`,
              },
            ];
          }
          return prev;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading tables for site/floor:', err);
          setNextTableNumber(1);
          
          // Log API error
          loggingService.logError(
            'table_onboarding_load_tables_error',
            `Failed to load tables for site ${site} floor ${floorNum}: ${err.message || 'Unknown error'}`,
            err,
            {
              endpoint: '/tables/tables',
              method: 'GET',
              siteId: site,
              floorNo: floorNum,
            }
          ).catch(logError => {
            console.warn('[OnboardTable] Failed to log error:', logError);
          });
        }
      } finally {
        if (!cancelled) setIsLoadingTablesForFloor(false);
      }
    };

    loadTablesForSiteAndFloor();
    return () => {
      cancelled = true;
    };
  }, [site, floor]);

  // Load sites on mount
  useEffect(() => {
    const loadSites = async () => {
      try {
        setIsLoadingSites(true);
        const response = await getAllSites();
        const sitesData = Array.isArray(response) 
          ? response 
          : (response?.sites || response?.data || []);
        setSites(sitesData);
        
        // Log successful API call
        loggingService.logInfo(
          'table_onboarding_load_sites',
          `Successfully loaded ${sitesData.length} sites for table onboarding`,
          {
            endpoint: '/sites',
            method: 'GET',
            siteCount: sitesData.length,
          }
        ).catch(logError => {
          console.warn('[OnboardTable] Failed to log event:', logError);
        });
      } catch (error) {
        console.error('Error loading sites:', error);
        
        // Log API error
        loggingService.logError(
          'table_onboarding_load_sites_error',
          `Failed to load sites for table onboarding: ${error.message || 'Unknown error'}`,
          error,
          {
            endpoint: '/sites',
            method: 'GET',
          }
        ).catch(logError => {
          console.warn('[OnboardTable] Failed to log error:', logError);
        });
      } finally {
        setIsLoadingSites(false);
      }
    };
    loadSites();
  }, []);

  const handleAddTable = () => {
    if (tables.length >= maxTables) return;

    const nextId = tables.length > 0 ? Math.max(...tables.map((t) => t.id)) + 1 : 1;
    // Continue table number from existing tables for this site+floor
    const suggestedTableNumber = nextTableNumber + tables.length;
    const defaultLabel = `T-${suggestedTableNumber}`;

    setTables([
      ...tables,
      { id: nextId, tableNumber: String(suggestedTableNumber).padStart(2, '0'), tableLabel: defaultLabel, seater: '' },
    ]);
  };

  const handleRemoveTable = (id) => {
    if (tables.length === 1) return;
    setTables(tables.filter((t) => t.id !== id));
  };

  const handleTableChange = (id, field, value) => {
    setTables((prev) =>
      prev.map((t) =>
        t.id === id
          ? {
              ...t,
              [field]: field === 'seater' ? value.replace(/[^0-9]/g, '') : value,
            }
          : t
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission - if already submitting, return early
    if (isSubmitting) return;
    
    // Don't submit in view mode unless editing
    if (mode === 'view' && !isEditing) return;

    // Validation
    if (!site) {
      setSubmitError('Please select a site');
      return;
    }

    const floorNumber = Number(floor);
    if (!floor || isNaN(floorNumber) || floorNumber <= 0) {
      setSubmitError('Please enter a valid floor number');
      return;
    }

    if (!block || !block.trim()) {
      setSubmitError('Please enter a block / section');
      return;
    }

    // Validate tables - must have label and seater
    const validTables = tables.filter(t => {
      const hasLabel = t.tableLabel && t.tableLabel.trim();
      const hasSeater = t.seater && Number(t.seater) > 0;
      return hasLabel && hasSeater;
    });
    
    if (validTables.length === 0) {
      setSubmitError('Please add at least one table with label and seater');
      return;
    }

    // Check if any table is missing label or seater
    const tablesWithMissingFields = tables.filter(t => {
      const hasLabel = t.tableLabel && t.tableLabel.trim();
      const hasSeater = t.seater && Number(t.seater) > 0;
      return !hasLabel || !hasSeater;
    });

    if (tablesWithMissingFields.length > 0) {
      setSubmitError('All tables must have both label and seater filled');
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setSubmitSuccess(false);

      // Store current mode before async operations
      const currentMode = mode;

      // Split into existing tables (have table_no) and new tables (no table_no — e.g. added in edit mode)
      const tablesToUpdate = validTables.filter(t => t.table_no != null && t.table_no !== undefined);
      const tablesToCreate = validTables.filter(t => t.table_no == null || t.table_no === undefined);
      const hasExistingTables = tablesToUpdate.length > 0;
      const hasNewTables = tablesToCreate.length > 0;

      // In edit mode, detect tables that were deleted (existed in originalData but not in current tables)
      const tablesToDelete = [];
      if (isEditing && originalData && originalData.tables) {
        const currentTableNos = new Set(
          validTables
            .filter(t => t.table_no != null && t.table_no !== undefined)
            .map(t => t.table_no)
        );
        
        originalData.tables.forEach(originalTable => {
          // Only delete tables that had a table_no (existing tables)
          if (originalTable.table_no != null && originalTable.table_no !== undefined) {
            // Check if this table is no longer in the current tables
            if (!currentTableNos.has(originalTable.table_no)) {
              tablesToDelete.push(originalTable);
            }
          }
        });
      }

      if (hasExistingTables || hasNewTables || tablesToDelete.length > 0) {
        const allErrors = [];

        // DELETE: tables that were removed in edit mode
        if (tablesToDelete.length > 0) {
          const deletePromises = tablesToDelete.map(async (table) => {
            try {
              // Use table_id if available (from originalData.originalData), otherwise use table_no + floor_no
              const originalTableData = table.originalData || {};
              const tableId = originalTableData.table_id || table.table_id;
              const tableIdentifier = tableId || table.table_no;
              
              // Only attempt deletion if we have a valid identifier
              if (!tableIdentifier) {
                throw new Error('No table identifier available for deletion');
              }
              
              // If we have table_id, we don't need floor_no; otherwise use table_no + floor_no
              const deleteResult = await tableBookingApi.deleteTable(
                site,
                tableIdentifier,
                tableId ? null : floorNumber
              );
              
              // Log successful API call
              loggingService.logInfo(
                'table_onboarding_delete_table',
                `Successfully deleted table ${tableIdentifier} for site ${site}`,
                {
                  endpoint: '/tables/tables',
                  method: 'DELETE',
                  siteId: site,
                  tableIdentifier: tableIdentifier,
                  floorNo: tableId ? null : floorNumber,
                }
              ).catch(logError => {
                console.warn('[OnboardTable] Failed to log event:', logError);
              });
              
              return deleteResult;
            } catch (deleteErr) {
              console.error('Error deleting table:', deleteErr);
              
              // Log API error
              loggingService.logError(
                'table_onboarding_delete_table_error',
                `Failed to delete table ${tableIdentifier} for site ${site}: ${deleteErr.message || 'Unknown error'}`,
                deleteErr,
                {
                  endpoint: '/tables/tables',
                  method: 'DELETE',
                  siteId: site,
                  tableIdentifier: tableIdentifier,
                }
              ).catch(logError => {
                console.warn('[OnboardTable] Failed to log error:', logError);
              });
              
              // Don't throw - let Promise.allSettled handle it
              throw deleteErr;
            }
          });
          
          const deleteResults = await Promise.allSettled(deletePromises);
          const deleteErrors = deleteResults
            .filter(r => r.status === 'rejected')
            .map(r => r.reason?.message || 'Unknown error');
          
          if (deleteErrors.length > 0) {
            // Add warning but don't block submission - deletion failures are non-critical
            // User can manually delete later if needed
            allErrors.push(...deleteErrors.map(err => `Warning: Could not delete table (${err}). You may need to delete it manually.`));
          }
        }

        // UPDATE: existing tables that have table_no
        if (hasExistingTables) {
          const updatePromises = tablesToUpdate.map(async (table) => {
            const updateData = {
              capacity: Number(table.seater),
              floor_no: floorNumber,
              section: block.trim(),
            };
            if (table.tableLabel && table.tableLabel.trim()) {
              updateData.table_label = table.tableLabel.trim();
            }
            const updateResult = await tableBookingApi.updateTableBySiteAndNumber(
              site,
              table.table_no,
              updateData,
              floorNumber
            );
            
            // Log successful API call
            loggingService.logInfo(
              'table_onboarding_update_table',
              `Successfully updated table ${table.table_no} for site ${site}`,
              {
                endpoint: '/tables/tables',
                method: 'PUT',
                siteId: site,
                tableNo: table.table_no,
                floorNo: floorNumber,
                capacity: updateData.capacity,
              }
            ).catch(logError => {
              console.warn('[OnboardTable] Failed to log event:', logError);
            });
            
            return updateResult;
          });
          const updateResults = await Promise.allSettled(updatePromises);
          const updateErrors = updateResults
            .filter(r => r.status === 'rejected')
            .map(r => r.reason?.message || 'Unknown error');
          allErrors.push(...updateErrors);
        }

        // CREATE: new tables (no table_no — e.g. added while editing)
        if (hasNewTables) {
          const floorNum = Number(floor);
          const isBulk = tablesToCreate.length > 1;
          let payload;
          if (isBulk) {
            const tablesWithIndices = tablesToCreate.map(t => {
              const originalIndex = tables.findIndex(orig => orig.id === t.id);
              const tableNo = t.tableNumber && t.tableNumber.trim()
                ? parseInt(t.tableNumber.trim())
                : originalIndex + 1;
              return {
                table_no: tableNo,
                capacity: Number(t.seater),
                table_label: t.tableLabel && t.tableLabel.trim() ? t.tableLabel.trim() : `T-${tableNo}`,
              };
            });
            payload = {
              site_id: site,
              floor_no: floorNum,
              section: block.trim(),
              tables: tablesWithIndices,
            };
          } else {
            const table = tablesToCreate[0];
            const tableNo = table.tableNumber && table.tableNumber.trim()
              ? parseInt(table.tableNumber.trim())
              : 1;
            payload = {
              site_id: site,
              table_no: tableNo,
              floor_no: floorNum,
              section: block.trim(),
              capacity: Number(table.seater),
              table_label: table.tableLabel && table.tableLabel.trim() ? table.tableLabel.trim() : `T-${tableNo}`,
            };
          }
          try {
            const response = await tableBookingApi.createTables(payload);
            if (response.errors && response.errors.length > 0) {
              allErrors.push(...response.errors.map(e => e.message || e));
            }
          } catch (createErr) {
            allErrors.push(createErr.message || 'Failed to create new tables');
          }
        }

        if (allErrors.length > 0) {
          setSubmitError(allErrors.join(', '));
          setIsSubmitting(false); // Re-enable button on error
        } else {
          setSubmitSuccess(true);
          setIsEditing(false);
          setOriginalData(null);
          
          // In onboard mode, navigate back to side panel (tables screen) after successful submission
          const isOnboardMode = currentMode === 'onboard' || mode === 'onboard';
          
          if (isOnboardMode) {
            // Show success message for 5 seconds before navigating
            setTimeout(() => {
              setShowSidePanel(true);
              setShowRightPanel(false);
              setMode(null);
              setSite('');
              setFloor('');
              setBlock('');
              setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
              setSubmitSuccess(false);
              setIsSubmitting(false);
              setRefreshKey(prev => prev + 1); // Trigger side panel refresh
            }, 3000);
          } else {
            // In edit/view mode, refresh side panel after successful update
            setRefreshKey(prev => prev + 1); // Trigger side panel refresh
            setTimeout(() => {
              setSubmitSuccess(false);
              setIsSubmitting(false); // Re-enable button after success message
            }, 3000);
          }
        }
      } else {
        // CREATE MODE: Create new tables
        // Determine if single or bulk creation
        const isBulk = validTables.length > 1;

        let payload;
        
        if (isBulk) {
          // Bulk creation format
          // Map valid tables while preserving original indices
          const tablesWithIndices = validTables.map(t => {
            const originalIndex = tables.findIndex(orig => orig.id === t.id);
            const tableNo = t.tableNumber && t.tableNumber.trim() 
              ? parseInt(t.tableNumber.trim()) 
              : originalIndex + 1;
            
            return {
              table_no: tableNo,
              capacity: Number(t.seater),
              table_label: t.tableLabel && t.tableLabel.trim() 
                ? t.tableLabel.trim() 
                : `T-${tableNo}`,
            };
          });

          payload = {
            site_id: site,
            floor_no: Number(floor),
            section: block.trim(),
            tables: tablesWithIndices,
          };
        } else {
          // Single table creation format
          const table = validTables[0];
          const tableNo = table.tableNumber && table.tableNumber.trim()
            ? parseInt(table.tableNumber.trim())
            : 1;

          payload = {
            site_id: site,
            table_no: tableNo,
            floor_no: Number(floor),
            section: block.trim(),
            capacity: Number(table.seater),
            table_label: table.tableLabel && table.tableLabel.trim()
              ? table.tableLabel.trim()
              : `T-${tableNo}`,
          };
        }

        const response = await tableBookingApi.createTables(payload);
        
        // Log successful API call
        loggingService.logInfo(
          'table_onboarding_create_tables',
          `Successfully created ${validTables.length} table(s) for site ${site} floor ${Number(floor)}`,
          {
            endpoint: '/tables/tables',
            method: 'POST',
            siteId: site,
            floorNo: Number(floor),
            section: block.trim(),
            tableCount: validTables.length,
            isBulk: isBulk,
          }
        ).catch(logError => {
          console.warn('[OnboardTable] Failed to log event:', logError);
        });
        
        // Determine if we're in onboard mode FIRST (before any response handling)
        const isOnboardMode = currentMode === 'onboard' || mode === 'onboard';
        
        // Check for errors in response - handle different response structures
        const hasErrors = response && (
          (response.errors && Array.isArray(response.errors) && response.errors.length > 0) ||
          (response.error && response.error) ||
          (response.message && response.message.toLowerCase().includes('error'))
        );
        
        if (hasErrors) {
          const errorMessage = response.errors 
            ? response.errors.map(e => e.message || e).join(', ')
            : response.error || response.message || 'Failed to create tables';
          setSubmitError(`Some tables failed to create: ${errorMessage}`);
          setIsSubmitting(false); // Re-enable button on error
        } else {
          // Success - no errors (or response doesn't indicate errors)
          setSubmitSuccess(true);
          
          // In onboard mode, navigate back to side panel (tables screen) after submission
          if (isOnboardMode) {
            // Show success message for 5 seconds before navigating
            setTimeout(() => {
              setShowSidePanel(true);
              setShowRightPanel(false);
              setMode(null);
              setSite('');
              setFloor('');
              setBlock('');
              setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
              setSubmitSuccess(false);
              setIsSubmitting(false);
              setRefreshKey(prev => prev + 1); // Trigger side panel refresh
            }, 3000);
          } else {
            // Reset form after successful submission
            setRefreshKey(prev => prev + 1); // Trigger side panel refresh
            setTimeout(() => {
              setSite('');
              setFloor('');
              setBlock('');
              setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
              setSubmitSuccess(false);
              if (mode === 'view') {
                setIsEditing(false);
              }
              setIsSubmitting(false); // Re-enable button after form reset
            }, 3000);
          }
        }
      }
    } catch (err) {
      console.error('=== ERROR IN SUBMISSION ===');
      console.error('Error submitting tables:', err);
      console.error('Error details:', err.message, err.stack);
      setSubmitError(err.message || 'Failed to submit tables. Please try again.');
      setIsSubmitting(false); // Re-enable button on error
    }
  };

  const panelStyle = {
    background:
      'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.40) 0%, rgba(255, 255, 255, 0.80) 97.94%)',
    boxShadow:
      '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(30px)',
  };

  const handleConfigurationSelect = (config) => {
    setSite(config.site);
    setFloor(config.floor);
    setBlock(config.block);
  };

  const handleFloorPlanSelect = (data) => {
    setShowRightPanel(true);
    setMode('view'); // View mode - don't show Add/Delete buttons
    setIsEditing(false); // Reset edit mode when selecting a new floor plan
    
    if (data && data.tables && data.tables.length > 0) {
      // Convert the tables from the floor plan to the format expected by the form
      // Preserve original table_no for updates
      const formattedTables = data.tables.map((table, index) => ({
        id: index + 1,
        tableNumber: table.tableNumber || String(table.table_no || index + 1).padStart(2, '0'),
        tableLabel: table.table_label || `T-${table.table_no || index + 1}`,
        seater: table.capacity || table.seater ? String(table.capacity || table.seater) : '',
        table_no: table.table_no, // Preserve for updates
        section: table.section, // Preserve section from table
        originalData: table, // Keep original data for reference
      }));
      setTables(formattedTables);
      setSelectedFloorPlan(data.floorPlan);
      
      // Extract section from tables or floorPlan (prefer floorPlan, fallback to first table)
      let section = '';
      if (data.floorPlan && data.floorPlan.section) {
        section = data.floorPlan.section;
      } else if (data.tables.length > 0 && data.tables[0].section) {
        section = data.tables[0].section;
      }
      
      // Set site, floor, and block from floor plan data
      if (data.floorPlan) {
        if (data.floorPlan.site_id) setSite(data.floorPlan.site_id);
        if (data.floorPlan.floor_no) setFloor(String(data.floorPlan.floor_no));
      }
      // Set block/section from extracted section
      if (section) {
        setBlock(section);
      }
    } else {
      // Reset to default if no tables
      setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
      setSelectedFloorPlan(null);
    }
  };

  const handleOnboardClick = () => {
    setShowRightPanel(true);
    setShowSidePanel(false); // Hide side panel when onboarding
    setMode('onboard'); // Onboard mode - show Add/Delete buttons, hide Edit button
    setIsEditing(false); // Reset edit mode
    // Reset form for new onboarding
    setSite('');
    setFloor('');
    setBlock('');
    setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
    setSelectedFloorPlan(null);
  };

  const handleEditClick = () => {
    // Save current state before entering edit mode
    setOriginalData({
      site,
      floor,
      block,
      tables: JSON.parse(JSON.stringify(tables)), // Deep copy
    });
    setIsEditing(true); // Enable edit mode
  };

  const handleCancelClick = () => {
    // Restore original data
    if (originalData) {
      setSite(originalData.site);
      setFloor(originalData.floor);
      setBlock(originalData.block);
      setTables(originalData.tables);
    }
    setIsEditing(false); // Exit edit mode
    setOriginalData(null); // Clear saved data
  };

  const handleBackClick = () => {
    setShowSidePanel(true); // Show side panel when back is clicked
    setShowRightPanel(false); // Hide right panel
    setMode(null); // Reset mode
    setIsEditing(false); // Reset edit mode
  };

  const navigateToTablesScreen = () => {
    setShowSidePanel(true);
    setShowRightPanel(false);
    setMode(null);
    setSite('');
    setFloor('');
    setBlock('');
    setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
    setSubmitSuccess(false);
    setIsSubmitting(false);
  };

  const handleDeleteSectionClick = () => {
    setShowDeleteSectionConfirm(true);
  };

  const handleDeleteSectionConfirm = async () => {
    if (!site || !block?.trim()) {
      setSubmitError('Site and Block/Section are required to delete.');
      setShowDeleteSectionConfirm(false);
      return;
    }
    try {
      setIsDeletingSection(true);
      setSubmitError(null);
      const floorNum = floor ? Number(floor) : null;
      const floorNoForDelete = isNaN(floorNum) || floorNum <= 0 ? undefined : floorNum;
      const res = await tableBookingApi.deleteSection(site, block.trim(), floorNoForDelete);
      
      // Log successful API call
      loggingService.logInfo(
        'table_onboarding_delete_section',
        `Successfully deleted section ${block.trim()} for site ${site}${floorNoForDelete ? ` floor ${floorNoForDelete}` : ''}`,
        {
          endpoint: '/tables/sections',
          method: 'DELETE',
          siteId: site,
          section: block.trim(),
          floorNo: floorNoForDelete,
          tablesDeleted: res?.tables_deleted || 0,
        }
      ).catch(logError => {
        console.warn('[OnboardTable] Failed to log event:', logError);
      });
      setShowDeleteSectionConfirm(false);
      setIsEditing(false);
      setOriginalData(null);
      setSubmitSuccess(true);
      setSubmitError(null);
      setTables([{ id: 1, tableNumber: '', tableLabel: 'T-1', seater: '' }]);
      setBlock('');
      if (res?.tables_deleted != null) {
        setSubmitSuccess(true);
        setSubmitError(null);
      }
      setShowSidePanel(true);
      setShowRightPanel(false);
      setMode(null);
    } catch (err) {
      setSubmitError(err?.message || 'Failed to delete section.');
      setShowDeleteSectionConfirm(false);
      
      // Log API error
      const floorNum = floor ? Number(floor) : null;
      const floorNoForDelete = isNaN(floorNum) || floorNum <= 0 ? undefined : floorNum;
      loggingService.logError(
        'table_onboarding_delete_section_error',
        `Failed to delete section ${block.trim()} for site ${site}: ${err.message || 'Unknown error'}`,
        err,
        {
          endpoint: '/tables/sections',
          method: 'DELETE',
          siteId: site,
          section: block.trim(),
          floorNo: floorNoForDelete,
        }
      ).catch(logError => {
        console.warn('[OnboardTable] Failed to log error:', logError);
      });
    } finally {
      setIsDeletingSection(false);
    }
  };

  const handleDeleteSectionCancel = () => {
    setShowDeleteSectionConfirm(false);
  };

  return (
    <div className="w-full h-full flex items-stretch gap-2">
      {/* Left Side Panel */}
      {showSidePanel && (
        <div className="flex-shrink-0 h-full w-[320px]">
          <TablesSidePanel 
            onSelectConfiguration={handleConfigurationSelect}
            onSelectFloorPlan={handleFloorPlanSelect}
            onOnboardClick={handleOnboardClick}
            refreshKey={refreshKey}
          />
        </div>
      )}

      {/* Right Side - Main Form */}
      {showRightPanel && (
      <div className="flex-1 min-w-0 h-full">
        <Card
          className="w-full h-full rounded-xl overflow-hidden flex flex-col gap-0"
          style={panelStyle}
        >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/30 bg-white/40 backdrop-blur-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            {mode === 'onboard' && (
              <button
                onClick={handleBackClick}
                className="p-2 rounded-lg hover:bg-white/60 transition-colors cursor-pointer"
                aria-label="Back to side panel"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
            )}
            <h2 className={`${h4} text-gray-900 font-semibold`}>Onboard tables</h2>
          </div>
          {mode === 'view' && !isEditing && (
            <IconTextButton
              icon={<Edit className="w-5 h-5" />}
              text="Edit table"
              onClick={handleEditClick}
              size="sm"
              variant="default"
            />
          )}
          {mode === 'view' && isEditing && (
            <button
              onClick={handleCancelClick}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-sm font-medium shadow-sm transition-colors [color:white] [&_svg]:[stroke:white]"
            >
              <X className="w-4 h-4 shrink-0" />
              <span>Cancel</span>
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          {/* Success/Error Messages */}
          {submitSuccess && (
            <div className="mx-6 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {mode === 'view' && isEditing ? 'Tables updated successfully!' : 'Tables created successfully!'}
            </div>
          )}
          {submitError && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {submitError}
            </div>
          )}

          {/* Location details */}
          <div className="px-4 pt-6 pb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className={`block ${sh2} text-gray-700 mb-2`}>
                Site <span className="text-red-500">*</span>
              </label>
              <select
                value={site}
                onChange={(e) => setSite(e.target.value)}
                disabled={mode === 'view' && !isEditing}
                required
                className={INPUT_BASE_CLASS}
              >
                <option value="">
                  {isLoadingSites ? 'Loading sites...' : 'Select a site'}
                </option>
                {Array.isArray(sites) && [...sites]
                  .sort((a, b) => {
                    const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
                    const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
                    return nameA.localeCompare(nameB);
                  })
                  .map((site) => (
                    <option key={site.site_id || site.id} value={site.site_id || site.id}>
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label className={`block ${sh2} text-gray-700 mb-2`}>
                Floor Number <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                inputMode="numeric"
                value={floor}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow empty string or valid positive integers
                  if (value === '' || (!isNaN(value) && Number(value) > 0)) {
                    setFloor(value);
                  }
                }}
                placeholder="Enter floor number"
                min="1"
                required
                disabled={mode === 'view' && !isEditing}
                className={INPUT_BASE_CLASS}
              />
            </div>
            <div>
              <label className={`block ${sh2} text-gray-700 mb-2`}>
                Block / Section <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={block}
                onChange={(e) => setBlock(e.target.value)}
                placeholder="Enter block / section"
                required
                disabled={mode === 'view' && !isEditing}
                className={INPUT_BASE_CLASS}
              />
            </div>
          </div>

          {/* Tables list */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className={`${sh2} text-gray-800 font-semibold`}>Tables</h3>
                {selectedFloorPlan && (
                  <p className="text-xs text-gray-500 mt-1">
                    Showing tables from selected floor plan
                  </p>
                )}
              </div>
              {(mode === 'onboard' || isEditing) && (
                <button
                  type="button"
                  onClick={handleAddTable}
                  disabled={tables.length >= maxTables}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-[10px] bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium shadow-sm transition-colors [color:white] [&_svg]:[stroke:white]"
                >
                  <Plus className="w-4 h-4 shrink-0" strokeWidth={2} />
                  <span>Add table</span>
                </button>
              )}
            </div>
            {tables.length >= maxTables && (
              <p className="text-xs text-red-500 mb-2">
                You have reached the maximum of {maxTables} tables.
              </p>
            )}
            {selectedFloorPlan && tables.length === 0 && (
              <p className="text-xs text-gray-500 mb-2">
                No tables found for the selected floor plan.
              </p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-h overflow-y-auto pb-2">
              {tables.map((table, index) => (
                <div
                  key={table.id}
                  className="rounded-xl border border-gray-200 bg-white/80 shadow-sm p-4 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-800">
                      Table {index + 1}
                    </span>
                    {(mode === 'onboard' || isEditing) && (
                      <IconButton
                        type="button"
                        size="sm"
                        variant="danger"
                        disabled={tables.length === 1}
                        onClick={() => handleRemoveTable(table.id)}
                        icon={<Trash2 className="w-3 h-3 text-red-700" strokeWidth={2} />}
                        aria-label="Remove table"
                      />
                    )}
                  </div>

                  <div className="grid gap-3">
                    {/* <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Table number
                      </label>
                      <input
                        type="text"
                        value={table.tableNumber}
                        onChange={(e) =>
                          handleTableChange(table.id, 'tableNumber', e.target.value)
                        }
                        placeholder={`e.g. ${String(index + 1).padStart(2, '0')}`}
                        className={`${INPUT_BASE_CLASS} py-1.5 text-sm`}
                      />
                    </div> */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Table label <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={table.tableLabel || `T-${index + 1}`}
                        onChange={(e) =>
                          handleTableChange(table.id, 'tableLabel', e.target.value)
                        }
                        placeholder={`e.g. T-${index + 1}`}
                        required
                        disabled={mode === 'view' && !isEditing}
                        className={`${INPUT_BASE_CLASS} py-1.5 text-sm`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Number of seater <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={table.seater}
                        onChange={(e) =>
                          handleTableChange(table.id, 'seater', e.target.value)
                        }
                        placeholder="e.g. 4"
                        required
                        disabled={mode === 'view' && !isEditing}
                        className={`${INPUT_BASE_CLASS} py-1.5 text-sm`}
                      />
                    </div>
                  </div>
                  
                </div>
              ))}
            </div>
            {/* Footer actions */}
          {(mode === 'onboard' || isEditing) && (
            <div className="mt-auto px-6 py-4 bg-white/70 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {mode === 'view' && isEditing && (
                  <button
                    type="button"
                    onClick={handleDeleteSectionClick}
                    disabled={isDeletingSection}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-sm font-semibold shadow-md transition-colors [color:white] [&_svg]:[stroke:white]"
                  >
                    <Trash2 className="w-4 h-4 shrink-0" strokeWidth={2} />
                    <span>Delete Block/Section</span>
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-6 py-2.5 rounded-[10px] bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-semibold shadow-md transition-colors [color:white] [&_svg]:[stroke:white]"
              >
                {isSubmitting 
                  ? (mode === 'view' && isEditing ? 'Updating...' : 'Creating...') 
                  : (mode === 'view' && isEditing ? 'Update' : 'Submit')}
              </button>
            </div>
          )}
          </div>

         
        </form>
        </Card>
      </div>
      )}

      {/* Delete Section confirmation overlay - portaled to viewport */}
      {showDeleteSectionConfirm &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 flex flex-col gap-4">
              <h3 className={`${h4} text-gray-900 font-semibold`}>Delete Block/Section</h3>
              <p className="text-sm text-gray-600">
                This will permanently delete all tables in the section &quot;{block}&quot;
                {floor ? ` on floor ${floor}` : ''}. This operation cannot be undone.
              </p>
              <p className="text-sm font-medium text-red-600">
                Are you sure you want to continue?
              </p>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleDeleteSectionCancel}
                  disabled={isDeletingSection}
                  className="px-4 py-2.5 rounded-[10px] border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSectionConfirm}
                  disabled={isDeletingSection}
                  className="px-4 py-2.5 rounded-[10px] bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white text-sm font-semibold transition-colors"
                >
                  {isDeletingSection ? 'Deleting...' : 'Delete section'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OnboardTable;

